import React, { useState, useEffect } from 'react';
import { Link, useParams, useNavigate } from 'react-router-dom';
import { Helmet } from 'react-helmet';
import PublicHeader from '../../components/PublicHeader';
import Footer from '../../components/Footer';
import { FiCalendar, FiChevronRight, FiClock, FiArrowUp, FiPrinter, FiAlertCircle } from 'react-icons/fi';
import { API_BASE_URL, BASE_URL, institute_id } from '../../config/config';

const PolicyPages = () => {
  const { slug } = useParams();
  const navigate = useNavigate();
  const [post, setPost] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showScrollTop, setShowScrollTop] = useState(false);
  const [readingProgress, setReadingProgress] = useState(0);

  useEffect(() => {
    fetchBlogPost();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [slug]);

  useEffect(() => {
    // Scroll to top when page changes
    window.scrollTo(0, 0);
    
    // Scroll tracking
    const handleScroll = () => {
      const totalHeight = document.documentElement.scrollHeight - window.innerHeight;
      const progress = (window.pageYOffset / totalHeight) * 100;
      setReadingProgress(progress);
      setShowScrollTop(window.pageYOffset > 400);
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const fetchBlogPost = async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await fetch(`${API_BASE_URL}/pages/${slug}?institute_id=${institute_id}`);
      const data = await response.json();
      
      if (data.success && data.data && data.data.length > 0) {
        const foundPost = data.data.find(p => p.url === slug) || data.data[0];
        if (foundPost) {
          setPost(foundPost);
        } else {
          setError('Page not found');
        }
      } else {
        setError('Page not found');
      }
    } catch (error) {
      console.error('Error fetching page:', error);
      setError('Failed to load page. Please try again later.');
    } finally {
      setLoading(false);
    }
  };

  const scrollToTop = () => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const handlePrint = () => {
    window.print();
  };


  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50">
        <PublicHeader />
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
          <div className="animate-pulse space-y-8">
            {/* Breadcrumb skeleton */}
            <div className="flex gap-2">
              <div className="bg-gray-200 h-4 w-20 rounded"></div>
              <div className="bg-gray-200 h-4 w-4 rounded"></div>
              <div className="bg-gray-200 h-4 w-24 rounded"></div>
            </div>
            
            {/* Title skeleton */}
            <div className="space-y-3">
              <div className="bg-gray-300 h-10 w-3/4 rounded-lg"></div>
              <div className="bg-gray-200 h-4 w-1/3 rounded"></div>
            </div>
            
            {/* Image skeleton */}
            <div className="bg-gray-200 h-96 rounded-xl"></div>
            
            {/* Content skeleton */}
            <div className="space-y-3">
              <div className="bg-gray-200 h-4 w-full rounded"></div>
              <div className="bg-gray-200 h-4 w-full rounded"></div>
              <div className="bg-gray-200 h-4 w-4/5 rounded"></div>
              <div className="bg-gray-200 h-4 w-full rounded"></div>
              <div className="bg-gray-200 h-4 w-3/4 rounded"></div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (error || !post) {
    return (
      <div className="min-h-screen bg-gray-50">
        <PublicHeader />
        <div className="max-w-xl mx-auto px-2 sm:px-6 lg:px-12 py-12">
          <div className="bg-white rounded-lg shadow-md p-12 text-center">
            <div className="flex justify-center mb-6">
              <div className="w-20 h-20 bg-red-100 rounded-full flex items-center justify-center">
                <FiAlertCircle className="w-10 h-10 text-red-600" />
              </div>
            </div>
            <h2 className="text-2xl font-bold text-gray-900 mb-3">
              {error || 'Page Not Found'}
            </h2>
            <p className="text-gray-600 mb-6">
              The page you're looking for doesn't exist or has been removed.
            </p>
            <div className="flex gap-4 justify-center">
              <button
                onClick={() => navigate(-1)}
                className="px-6 py-3 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors"
              >
                Go Back
              </button>
              <Link
                to="/"
                className="px-6 py-3 bg-primary-600 text-white rounded-lg hover:bg-primary-700 transition-colors"
              >
                Back to Home
              </Link>
            </div>
          </div>
        </div>
        <Footer />
      </div>
    );
  }

  const pageImage = post.feature_image || post.image;
  const pageTitle = post.meta_title || post.title;
  const pageDescription = post.meta_description || post.description?.substring(0, 160).replace(/<[^>]*>/g, '');

  return (
    <div className="min-h-screen bg-gray-50">
      {/* SEO Meta Tags */}
      <Helmet>
        <title>{pageTitle}</title>
        <meta name="description" content={pageDescription} />
        {post.meta_keyword && <meta name="keywords" content={post.meta_keyword} />}
        <meta property="og:title" content={pageTitle} />
        <meta property="og:description" content={pageDescription} />
        {pageImage && <meta property="og:image" content={pageImage} />}
        <meta property="og:type" content="article" />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content={pageTitle} />
        <meta name="twitter:description" content={pageDescription} />
        {pageImage && <meta name="twitter:image" content={pageImage} />}
      </Helmet>

      {/* Reading Progress Bar */}
      <div 
        className="fixed top-0 left-0 h-1 bg-primary-600 z-50 transition-all duration-200"
        style={{ width: `${readingProgress}%` }}
      />

      <PublicHeader />

      <article className="py-12 print:py-4">
        <div className="max-w-12xl mx-auto px-4 sm:px-6 lg:px-8">
          {/* Breadcrumb */}
          <nav className="flex items-center gap-2 text-sm text-gray-600 mb-6 print:hidden" aria-label="Breadcrumb">
            <Link to="/" className="hover:text-primary-600 transition-colors">Home</Link>
            <FiChevronRight className="w-4 h-4" />
            <span className="text-gray-900 font-medium">{post.title}</span>
          </nav>

          {/* Action Buttons */}
          {/* <div className="flex justify-end gap-3 mb-6 print:hidden">
            <button
              onClick={handlePrint}
              className="flex items-center gap-2 px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors"
              aria-label="Print page"
            >
              <FiPrinter className="w-4 h-4" />
              <span className="hidden sm:inline">Print</span>
            </button>
          </div> */}

          {/* Post Header */}
          <header className="mb-8 bg-white rounded-lg shadow-sm p-8 print:shadow-none print:p-0">
            <h1 className="text-3xl md:text-4xl lg:text-5xl font-bold text-gray-900 mb-4 leading-tight">
              {post.title}
            </h1>
            
            <div className="flex flex-wrap items-center gap-4 text-sm text-gray-600 border-t border-gray-200 pt-4 mt-4">
              {post.cdate && (
                <span className="flex items-center gap-2">
                  <FiCalendar className="w-4 h-4" />
                  <span>
                    Published: {new Date(post.cdate).toLocaleDateString('en-US', { 
                      year: 'numeric', 
                      month: 'long', 
                      day: 'numeric' 
                    })}
                  </span>
                </span>
              )}
              
              {post.udate && post.udate !== post.cdate && (
                <span className="flex items-center gap-2">
                  <FiClock className="w-4 h-4" />
                  <span>
                    Updated: {new Date(post.udate).toLocaleDateString('en-US', { 
                      year: 'numeric', 
                      month: 'long', 
                      day: 'numeric' 
                    })}
                  </span>
                </span>
              )}
            </div>
          </header>

          {/* Featured Image */}
          {pageImage && (
            <div className="mb-8 rounded-xl overflow-hidden shadow-lg print:shadow-none">
              <img
                src={pageImage}
                alt={post.title}
                className="w-full h-96 object-cover print:h-auto"
                onError={(e) => {
                  e.target.style.display = 'none';
                }}
              />
            </div>
          )}

          {/* Main Content */}
          <div className="bg-white rounded-lg shadow-sm p-8 md:p-12 print:shadow-none print:p-0">
            <div 
              className="prose prose-lg prose-gray max-w-none
                prose-headings:font-bold prose-headings:text-gray-900
                prose-h1:text-3xl prose-h1:mb-4 prose-h1:mt-8
                prose-h2:text-2xl prose-h2:mb-3 prose-h2:mt-6 prose-h2:border-b prose-h2:border-gray-200 prose-h2:pb-2
                prose-h3:text-xl prose-h3:mb-2 prose-h3:mt-4
                prose-p:text-gray-700 prose-p:leading-relaxed prose-p:mb-4
                prose-a:text-primary-600 prose-a:no-underline hover:prose-a:underline
                prose-strong:text-gray-900 prose-strong:font-semibold
                prose-ul:list-disc prose-ul:pl-6 prose-ul:mb-4
                prose-ol:list-decimal prose-ol:pl-6 prose-ol:mb-4
                prose-li:text-gray-700 prose-li:mb-2
                prose-blockquote:border-l-4 prose-blockquote:border-primary-500 prose-blockquote:pl-4 prose-blockquote:italic prose-blockquote:text-gray-700
                prose-code:text-primary-600 prose-code:bg-gray-100 prose-code:px-1 prose-code:py-0.5 prose-code:rounded
                prose-pre:bg-gray-900 prose-pre:text-gray-100
                prose-table:border-collapse prose-table:w-full
                prose-th:border prose-th:border-gray-300 prose-th:bg-gray-50 prose-th:p-2 prose-th:text-left
                prose-td:border prose-td:border-gray-300 prose-td:p-2
                print:prose-sm"
              dangerouslySetInnerHTML={{ __html: post.description }}
            />
          </div>

          {/* Last Updated Notice */}
          {post.udate && post.udate !== post.cdate && (
            <div className="mt-8 p-4 bg-blue-50 border-l-4 border-blue-500 rounded-r-lg print:hidden">
              <p className="text-sm text-blue-800">
                <strong>Note:</strong> This page was last updated on {new Date(post.udate).toLocaleDateString('en-US', { 
                  year: 'numeric', 
                  month: 'long', 
                  day: 'numeric' 
                })}. Please ensure you're viewing the most current version.
              </p>
            </div>
          )}
        </div>
      </article>

      {/* Scroll to Top Button */}
      {showScrollTop && (
        <button
          onClick={scrollToTop}
          className="fixed bottom-8 right-8 p-4 bg-primary-600 text-white rounded-full shadow-lg hover:bg-primary-700 transition-all duration-300 z-40 print:hidden"
          aria-label="Scroll to top"
        >
          <FiArrowUp className="w-5 h-5" />
        </button>
      )}
    
      <Footer />
    </div>
  );
};

export default PolicyPages;

